import * as THREE from 'three';

export function loadDecalTexture(fileOrUrl) {
    if (!fileOrUrl) return;

    const loadCallback = (url) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            let borderSize = Math.max(img.width / 10, 10);
            canvas.width = img.width + borderSize * 2;
            canvas.height = img.height + borderSize * 2;

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, borderSize, borderSize);

            // Use straight-alpha texture (no premultiply) to match Substance PNGs
            this.decalTexture = new THREE.CanvasTexture(canvas);
            this.decalTexture.wrapS = THREE.ClampToEdgeWrapping;
            this.decalTexture.wrapT = THREE.ClampToEdgeWrapping;
            this.decalTexture.minFilter = THREE.LinearFilter;
            this.decalTexture.magFilter = THREE.LinearFilter;
            this.decalTexture.generateMipmaps = false;
            this.decalTexture.premultiplyAlpha = false;
            this.decalTexture.needsUpdate = true;
            this.decalTexture.format = THREE.RGBAFormat;

            this.updateStatus('Decal texture loaded with safe border');

            if (fileOrUrl instanceof File) {
                this.addDecalToPicker(url);
            }
        };
        img.src = url;
    };

    if (typeof fileOrUrl === 'string') {
        loadCallback(fileOrUrl);
    } else {
        const reader = new FileReader();
        reader.onload = (e) => loadCallback(e.target.result);
        reader.readAsDataURL(fileOrUrl);
    }
}

export function onMouseClick(event) {
    if (this.transformControls?.object || !this.mesh || !this.decalTexture) return;

    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObject(this.mesh, true);

    if (intersects.length > 0) {
        const intersection = intersects[0];

        if (this.projectionMode === '2d' && intersection.uv) {
            this.createMoveableDecal(intersection);
        } else if (this.projectionMode === '3d') {
            this.create3DProjectorAtIntersection(intersection);
        }
    }
}

export function create3DProjectorAtIntersection(intersection) {
    if (!this.projective3D) {
        this.updateStatus('3D projection system not initialized');
        return;
    }

    const position = intersection.point.clone();
    const normal = intersection.face ? intersection.face.normal.clone() : new THREE.Vector3(0, 0, 1);
    const worldNormal = normal.transformDirection(intersection.object.matrixWorld).normalize();
    const target = position.clone().add(worldNormal.multiplyScalar(1.0));

    this.create3DProjector(position, target);
}

export function createMoveableDecal(intersection) {
    this.removeMoveableDecal();

    const opacity = document.getElementById('opacitySlider').value / 100;
    const scale = parseFloat(document.getElementById('scaleSlider').value);
    const worldPosition = intersection.point;
    const normal = intersection.face ? intersection.face.normal.clone() : new THREE.Vector3(0, 0, 1);
    const worldNormal = normal.clone().transformDirection(intersection.object.matrixWorld).normalize();
    const decalSize = 0.2 * scale;
    const projectorPosition = worldPosition.clone().add(worldNormal.clone().multiplyScalar(0.05));

    this.createDecalPreviewObject(projectorPosition, worldNormal, decalSize);

    this.currentDecal.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), worldNormal);

    this.transformControls.attach(this.currentDecal);
    this.transformControls.setMode('translate');

    const initialProjectionDirection = worldNormal.clone().negate();
    this.applyProjectiveDecal(projectorPosition, worldPosition, initialProjectionDirection, decalSize, opacity, null, intersection);

    this.currentDecal.userData = { intersection, opacity, scale, decalSize };
    this.updateStatus('Decal created! Use transform controls to position it, then click "Apply Decal" to make it permanent.');
}

export function createDecalPreviewObject(position, normal, size) {
    this.currentDecal = new THREE.Object3D();
    this.currentDecal.position.copy(position);
    this.currentDecal.lookAt(position.clone().add(normal));

    const wireframeGeometry = new THREE.PlaneGeometry(size * 0.8, size * 0.8);
    const wireframeMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00, transparent: true, opacity: 0.3,
        side: THREE.DoubleSide, depthTest: false, wireframe: true
    });
    const wireframe = new THREE.Mesh(wireframeGeometry, wireframeMaterial);
    this.currentDecal.add(wireframe);
    this.scene.add(this.currentDecal);
}

export function removeMoveableDecal() {
    if (!this.currentDecal) return;

    this.scene.remove(this.currentDecal);
    this.transformControls?.detach();

    if (this.originalMaterials) {
        this.mesh.traverse(child => {
            if (child.isMesh && this.originalMaterials.has(child)) {
                child.material = this.originalMaterials.get(child);
            }
        });
        this.originalMaterials.clear();
        this.originalMaterials = null;
    }

    [this.cachedDecalMaterial, this.meshDecalMaterials].forEach(cache => {
        if (cache) {
            if (cache.dispose) cache.dispose();
            else cache.forEach(material => material.dispose());
            if (cache.clear) cache.clear();
        }
    });
    this.cachedDecalMaterial = null;
    this.meshDecalMaterials = null;
    this.currentDecal = null;
    this.render();
}

export function calculateSurfaceAwareRotationMatrix(intersection, quaternion) {
    const rotationMatrix = new THREE.Matrix3();
    const euler = new THREE.Euler().setFromQuaternion(quaternion, 'XYZ');

    let surfaceRotation = 0;
    if (intersection?.face?.normal && intersection.face) {
        const face = intersection.face;
        const geometry = intersection.object.geometry;

        const v1 = geometry.attributes.position.array.slice(face.a * 3, face.a * 3 + 3);
        const v2 = geometry.attributes.position.array.slice(face.b * 3, face.b * 3 + 3);
        const v3 = geometry.attributes.position.array.slice(face.c * 3, face.c * 3 + 3);

        const uv1 = geometry.attributes.uv.array.slice(face.a * 2, face.a * 2 + 2);
        const uv2 = geometry.attributes.uv.array.slice(face.b * 2, face.b * 2 + 2);
        const uv3 = geometry.attributes.uv.array.slice(face.c * 2, face.c * 2 + 2);

        const deltaUV1 = [uv2[0] - uv1[0], uv2[1] - uv1[1]];
        const deltaUV2 = [uv3[0] - uv1[0], uv3[1] - uv1[1]];

        const deltaPos1 = [v2[0] - v1[0], v2[1] - v1[1], v2[2] - v1[2]];
        const deltaPos2 = [v3[0] - v1[0], v3[1] - v1[1], v3[2] - v1[2]];

        const det = deltaUV1[0] * deltaUV2[1] - deltaUV1[1] * deltaUV2[0];
        if (Math.abs(det) > 0.001) {
            const tangentU = [
                (deltaUV2[1] * deltaPos1[0] - deltaUV1[1] * deltaPos2[0]) / det,
                (deltaUV2[1] * deltaPos1[1] - deltaUV1[1] * deltaPos2[1]) / det,
                (deltaUV2[1] * deltaPos1[2] - deltaUV1[1] * deltaPos2[2]) / det
            ];
            surfaceRotation = Math.atan2(tangentU[2], tangentU[0]);
        }
    }

    const totalRotation = surfaceRotation - euler.z;
    const cos = Math.cos(totalRotation);
    const sin = Math.sin(totalRotation);
    rotationMatrix.set(cos, -sin, 0, sin, cos, 0, 0, 0, 1);

    return rotationMatrix;
}

export function updateProjectiveDecalPreview() {
    if (!this.currentDecal?.userData) return;

    const currentPosition = this.currentDecal.position.clone();
    const projectionDirection = new THREE.Vector3(0, 0, -1);
    projectionDirection.applyQuaternion(this.currentDecal.quaternion);
    const userData = this.currentDecal.userData;

    const safeScale = (val, def = 1) => isFinite(val) && val > 0 ? val : def;
    const safeScaleX = safeScale(this.currentDecal.scale.x);
    const safeScaleY = safeScale(this.currentDecal.scale.y);
    const safeDecalSize = safeScale(userData.decalSize, 0.2);

    const newDecalSizeX = safeDecalSize * safeScaleX;
    const newDecalSizeY = safeDecalSize * safeScaleY;

    const wireframe = this.currentDecal.children[0];
    if (wireframe) {
        const planeWidth = Math.max(0.001, safeDecalSize * safeScaleX * 0.8);
        const planeHeight = Math.max(0.001, safeDecalSize * safeScaleY * 0.8);
        const threshold = 0.001;

        if (!wireframe.userData) wireframe.userData = {};
        if (wireframe.userData.lastWidth === undefined) wireframe.userData.lastWidth = planeWidth;
        if (wireframe.userData.lastHeight === undefined) wireframe.userData.lastHeight = planeHeight;

        if (Math.abs(planeWidth - wireframe.userData.lastWidth) > threshold ||
            Math.abs(planeHeight - wireframe.userData.lastHeight) > threshold) {
            wireframe.geometry.dispose();
            wireframe.geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
            wireframe.userData.lastWidth = planeWidth;
            wireframe.userData.lastHeight = planeHeight;
        }
    }

    const projectorPosition = currentPosition.clone().add(projectionDirection.clone().multiplyScalar(0.05));

    let currentIntersection = userData.intersection;
    this.raycaster.set(currentPosition, projectionDirection);
    const intersects = this.raycaster.intersectObject(this.mesh, true);

    const positionChanged = userData.intersection?.point &&
        currentPosition.distanceTo(userData.intersection.point) > 0.05;

    if (intersects.length > 0) {
        for (const intersect of intersects) {
            if (intersect.uv) {
                if (positionChanged ||
                    (userData.intersection?.point && intersect.point.distanceTo(userData.intersection.point) < 0.1)) {
                    currentIntersection = intersect;
                    if (positionChanged) {
                        userData.intersection = intersect;
                    }
                }
                break;
            }
        }
    }

    const rotationMatrix = this.calculateSurfaceAwareRotationMatrix(currentIntersection, this.currentDecal.quaternion);

    this.applyProjectiveDecal(projectorPosition, currentPosition, projectionDirection,
        { x: newDecalSizeX, y: newDecalSizeY }, userData.opacity, rotationMatrix, currentIntersection);
}

export function getSafeSize(size) {
    if (typeof size === 'object') {
        return {
            x: isFinite(size.x) && size.x > 0 ? size.x : 0.2,
            y: isFinite(size.y) && size.y > 0 ? size.y : 0.2
        };
    }
    const safeSize = isFinite(size) && size > 0 ? size : 0.2;
    return { x: safeSize, y: safeSize };
}

export function bakeProjectiveDecalToTextures(intersection, projectorPos, projectionDirection, size, opacity, rotationMatrix = null) {
    const uv = intersection.uv;
    const { x: sizeX, y: sizeY } = this.getSafeSize(size);

    const uvDecalSizeX = sizeX * 1.0;
    const uvDecalSizeY = sizeY * 1.0;
    const uvMinU = uv.x - uvDecalSizeX / 2;
    const uvMaxU = uv.x + uvDecalSizeX / 2;
    const uvMinV = uv.y - uvDecalSizeY / 2;
    const uvMaxV = uv.y + uvDecalSizeY / 2;

    const minTileU = Math.floor(uvMinU);
    const maxTileU = Math.floor(uvMaxU);
    const minTileV = Math.floor(uvMinV);
    const maxTileV = Math.floor(uvMaxV);

    for (let tileU = minTileU; tileU <= maxTileU; tileU++) {
        for (let tileV = minTileV; tileV <= maxTileV; tileV++) {
            const udimTile = this.calculateUDIMTile(tileU, tileV);
            const targetChannelIndex = this.uvChannels.findIndex(channel => channel.udimTile === udimTile);

            if (targetChannelIndex !== -1) {
                this.bakeProjectiveDecalToUDIM(this.uvChannels[targetChannelIndex], targetChannelIndex,
                    projectorPos, projectionDirection, size, opacity, rotationMatrix, intersection);
            }
        }
    }

    const borderThreshold = 0.3;
    const localUInTile = uv.x - Math.floor(uv.x);
    const localVInTile = uv.y - Math.floor(uv.y);

    if (localUInTile < borderThreshold || localUInTile > (1 - borderThreshold) ||
        localVInTile < borderThreshold || localVInTile > (1 - borderThreshold)) {

        const centerU = Math.floor(uv.x);
        const centerV = Math.floor(uv.y);

        for (let deltaU = -1; deltaU <= 1; deltaU++) {
            for (let deltaV = -1; deltaV <= 1; deltaV++) {
                if (deltaU === 0 && deltaV === 0) continue;

                const neighborU = centerU + deltaU;
                const neighborV = centerV + deltaV;

                const neighborTile = this.calculateUDIMTile(neighborU, neighborV);
                const neighborChannelIndex = this.uvChannels.findIndex(channel => channel.udimTile === neighborTile);

                if (neighborChannelIndex !== -1) {
                    this.bakeProjectiveDecalToUDIM(this.uvChannels[neighborChannelIndex], neighborChannelIndex,
                        projectorPos, projectionDirection, size, opacity, rotationMatrix, intersection);
                }
            }
        }
    }

    this.updateAllTextures();
}

export function bakeProjectiveDecalToUDIM(channel, channelIndex, projectorPos, projectionDirection, size, opacity, rotationMatrix = null, intersection = null) {
    if (!this.decalTexture?.image) return;

    if (intersection?.uv) {
        this.bakeProjectiveDecalToUDIMSimple(channel, channelIndex, projectorPos, projectionDirection, size, opacity, rotationMatrix, intersection);
    }
}

export function bakeProjectiveDecalToUDIMSimple(channel, channelIndex, projectorPos, projectionDirection, size, opacity, rotationMatrix, intersection) {
    const canvas = channel.decalCanvas;
    const ctx = channel.decalContext;
    const intersectionUV = intersection.uv;
    if (!intersectionUV) return;

    const currentTileU = (channel.udimTile - 1001) % 10;
    const currentTileV = Math.floor((channel.udimTile - 1001) / 10);
    const relativeCenterU = intersectionUV.x - currentTileU;
    const relativeCenterV = intersectionUV.y - currentTileV;

    if (relativeCenterU < 0 || relativeCenterU > 1 || relativeCenterV < 0 || relativeCenterV > 1) return;

    const centerX = relativeCenterU * canvas.width;
    const centerY = (1 - relativeCenterV) * canvas.height;

    const { x: sizeX, y: sizeY } = this.getSafeSize(size);
    const canvasSizeX = sizeX * canvas.width;
    const canvasSizeY = sizeY * canvas.height;

    const expandedSizeU = sizeX * 1.42;
    const expandedSizeV = sizeY * 1.42;
    const expandedCanvasSizeX = expandedSizeU * canvas.width;
    const expandedCanvasSizeY = expandedSizeV * canvas.height;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvasSizeX;
    tempCanvas.height = canvasSizeY;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';

    const decalImg = this.decalTexture.image;
    tempCtx.drawImage(decalImg, 0, 0, canvasSizeX, canvasSizeY);

    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.globalAlpha = opacity;
    ctx.globalCompositeOperation = 'source-over';

    const sampleRadius = Math.sqrt(expandedCanvasSizeX * expandedCanvasSizeX + expandedCanvasSizeY * expandedCanvasSizeY) / 2;

    const startX = Math.max(0, Math.floor(centerX - sampleRadius));
    const endX = Math.min(canvas.width, Math.ceil(centerX + sampleRadius));
    const startY = Math.max(0, Math.floor(centerY - sampleRadius));
    const endY = Math.min(canvas.height, Math.ceil(centerY + sampleRadius));

    const imageData = ctx.getImageData(startX, startY, endX - startX, endY - startY);
    const data = imageData.data;

    for (let y = startY; y < endY; y++) {
        for (let x = startX; x < endX; x++) {
            const uvOffsetX = (x - centerX) / canvas.width;
            const uvOffsetY = (y - centerY) / canvas.height;

            const u = uvOffsetX / expandedSizeU + 0.5;
            const v = uvOffsetY / expandedSizeV + 0.5;

            if (u < 0 || u > 1 || v < 0 || v > 1) continue;

            let sampleU = u;
            let sampleV = v;

            if (rotationMatrix) {
                const centeredU = u - 0.5;
                const centeredV = v - 0.5;

                const rotatedU = rotationMatrix.elements[0] * centeredU + rotationMatrix.elements[1] * centeredV;
                const rotatedV = rotationMatrix.elements[3] * centeredU + rotationMatrix.elements[4] * centeredV;

                sampleU = rotatedU + 0.5;
                sampleV = rotatedV + 0.5;
            }

            if (sampleU < 0 || sampleU > 1 || sampleV < 0 || sampleV > 1) continue;

            const finalU = (sampleU - 0.5) * (expandedSizeU / sizeX) + 0.5;
            const finalV = (sampleV - 0.5) * (expandedSizeV / sizeY) + 0.5;

            if (finalU < 0 || finalU > 1 || finalV < 0 || finalV > 1) continue;

            const sampleX = Math.floor(finalU * (canvasSizeX - 1));
            const sampleY = Math.floor(finalV * (canvasSizeY - 1));

            if (sampleX >= 0 && sampleX < canvasSizeX && sampleY >= 0 && sampleY < canvasSizeY) {
                const tempImageData = tempCtx.getImageData(sampleX, sampleY, 1, 1);
                const srcPixel = tempImageData.data;

                const pixelIndex = ((y - startY) * (endX - startX) + (x - startX)) * 4;

                const alpha = (srcPixel[3] / 255) * opacity;
                data[pixelIndex] = srcPixel[0] * alpha + data[pixelIndex] * (1 - alpha);
                data[pixelIndex + 1] = srcPixel[1] * alpha + data[pixelIndex + 1] * (1 - alpha);
                data[pixelIndex + 2] = srcPixel[2] * alpha + data[pixelIndex + 2] * (1 - alpha);
                data[pixelIndex + 3] = Math.min(255, data[pixelIndex + 3] + srcPixel[3] * opacity);
            }
        }
    }

    ctx.putImageData(imageData, startX, startY);

    ctx.restore();
    this.compositeTexture(channelIndex);
}

export function updateAllTextures() {
    this.uvChannels.forEach(channel => channel.texture.needsUpdate = true);
}

export function updateProjectiveDecalMaterialUniforms(material, projectorPos, projectionDirection, size, opacity, rotationMatrix = null, intersectionUV = null) {
    const finalRotationMatrix = rotationMatrix || new THREE.Matrix3().identity?.() || new THREE.Matrix3();
    const { x: sizeX, y: sizeY } = this.getSafeSize(size);

    if (material.uniforms) {
        if (intersectionUV) {
            material.uniforms.decalCenterUV.value.set(intersectionUV.x, intersectionUV.y);
        }
        material.uniforms.decalSizeU.value = sizeX;
        material.uniforms.decalSizeV.value = sizeY;
        material.uniforms.decalOpacity.value = opacity;
        if (material.uniforms.rotationMatrix?.value?.copy) {
            material.uniforms.rotationMatrix.value.copy(finalRotationMatrix);
        }

        this.uvChannels.forEach(channel => {
            const udimKey = `compositeTexture${channel.udimTile}`;
            if (material.uniforms[udimKey]) {
                material.uniforms[udimKey].value = channel.texture;
            }
        });
    }
}

export function applyProjectiveDecal(projectorPos, targetPos, projectionDirection, size, opacity, rotationMatrix = null, intersection = null) {
    if (!this.currentDecal) return;

    let intersectionUV = null;
    if (intersection?.uv) {
        intersectionUV = intersection.uv;
    } else if (this.currentDecal.userData?.intersection?.uv) {
        intersectionUV = this.currentDecal.userData.intersection.uv;
    }

    if (!this.cachedDecalMaterial) {
        this.cachedDecalMaterial = this.createProjectiveDecalMaterial(projectorPos, projectionDirection, size, opacity, rotationMatrix, intersectionUV);
    } else {
        this.updateProjectiveDecalMaterialUniforms(this.cachedDecalMaterial, projectorPos, projectionDirection, size, opacity, rotationMatrix, intersectionUV);
    }

    if (!this.originalMaterials) {
        this.originalMaterials = new Map();
    }

    this.mesh.traverse(child => {
        if (child.isMesh) {
            if (!this.originalMaterials.has(child)) {
                this.originalMaterials.set(child, child.material);
            }
            this.applyProjectiveDecalToMesh(child, this.cachedDecalMaterial, projectorPos, projectionDirection, size, intersectionUV);
        }
    });

    this.render();
}

export function createProjectiveDecalMaterial(projectorPos, projectionDirection, size, opacity, rotationMatrix = null, intersectionUV = null) {
    const finalRotationMatrix = rotationMatrix || new THREE.Matrix3().identity?.() || new THREE.Matrix3();
    const { x: sizeX, y: sizeY } = this.getSafeSize(size);
    const uvCenter = intersectionUV ? new THREE.Vector2(intersectionUV.x, intersectionUV.y) : new THREE.Vector2(0.5, 0.5);

    const uniforms = {
        baseTexture: { value: null },
        compositeTexture1001: { value: null },
        compositeTexture1002: { value: null },
        compositeTexture1003: { value: null },
        compositeTexture1011: { value: null },
        compositeTexture1012: { value: null },
        compositeTexture1013: { value: null },
        decalTexture: { value: this.decalTexture },
        decalCenterUV: { value: uvCenter },
        decalSizeU: { value: sizeX },
        decalSizeV: { value: sizeY },
        decalOpacity: { value: opacity },
        rotationMatrix: { value: finalRotationMatrix },
        useComposite: { value: true }
    };

    this.uvChannels.forEach(channel => {
        const udimKey = `compositeTexture${channel.udimTile}`;
        if (uniforms[udimKey] !== undefined) {
            uniforms[udimKey].value = channel.texture;
        }
    });

    const defaultTexture = this.createDefaultTexture();
    Object.keys(uniforms).forEach(key => {
        if (key.startsWith('compositeTexture') && !uniforms[key].value) {
            uniforms[key].value = defaultTexture;
        }
    });

    return new THREE.ShaderMaterial({
        vertexShader: this.vertexShader || '',
        fragmentShader: this.fragmentShader || '',
        uniforms,
        transparent: true,
        side: THREE.DoubleSide
    });
}

export function applyProjectiveDecalToMesh(mesh, decalMaterial, projectorPos, projectionDirection, size, intersectionUV = null) {
    if (!this.meshDecalMaterials) {
        this.meshDecalMaterials = new Map();
    }

    let meshDecalMaterial = this.meshDecalMaterials.get(mesh);

    if (!meshDecalMaterial) {
        meshDecalMaterial = decalMaterial.clone();
        this.meshDecalMaterials.set(mesh, meshDecalMaterial);

        if (mesh.material?.map) {
            meshDecalMaterial.uniforms.baseTexture.value = mesh.material.map;
        } else if (this.uvChannels.length > 0) {
            meshDecalMaterial.uniforms.baseTexture.value = this.createDefaultTexture();
        }
    } else {
        this.updateProjectiveDecalMaterialUniforms(meshDecalMaterial, projectorPos, projectionDirection, size,
            decalMaterial.uniforms.decalOpacity.value, decalMaterial.uniforms.rotationMatrix.value, intersectionUV);
    }

    mesh.material = meshDecalMaterial;
}

export function applyMoveableDecal() {
    if (!this.currentDecal?.userData) {
        this.updateStatus('No decal to apply');
        return;
    }

    const currentPosition = this.currentDecal.position.clone();
    const userData = this.currentDecal.userData;

    const direction = new THREE.Vector3(0, 0, -1);
    direction.applyQuaternion(this.currentDecal.quaternion);
    const raycaster = new THREE.Raycaster();
    raycaster.set(currentPosition, direction);
    const intersects = raycaster.intersectObject(this.mesh, true);

    let targetIntersection = userData.intersection;

    const positionChanged = userData.intersection?.point &&
        currentPosition.distanceTo(userData.intersection.point) > 0.05;

    if (intersects.length > 0) {
        for (const intersect of intersects) {
            if (intersect.uv) {
                if (positionChanged ||
                    (userData.intersection?.point && intersect.point.distanceTo(userData.intersection.point) < 0.1)) {
                    targetIntersection = intersect;
                }
                break;
            }
        }
    }

    const safeScale = (val, def = 1) => isFinite(val) && val > 0 ? val : def;
    const safeScaleX = safeScale(this.currentDecal.scale.x);
    const safeScaleY = safeScale(this.currentDecal.scale.y);
    const safeDecalSize = safeScale(userData.decalSize, 0.2);

    const finalDecalSizeX = safeDecalSize * safeScaleX;
    const finalDecalSizeY = safeDecalSize * safeScaleY;

    const rotationMatrix = this.calculateSurfaceAwareRotationMatrix(targetIntersection, this.currentDecal.quaternion);

    const projectorPosition = currentPosition.clone().add(direction.clone().multiplyScalar(0.05));

    this.removeMoveableDecal();
    this.bakeProjectiveDecalToTextures(targetIntersection, projectorPosition, direction,
        { x: finalDecalSizeX, y: finalDecalSizeY }, userData.opacity, rotationMatrix);
    this.refreshCharacterMaterials();
    this.updateStatus('Decal applied');
}

export function cancelMoveableDecal() {
    this.removeMoveableDecal();
}

export function clearDecals() {
    this.uvChannels.forEach((channel, index) => {
        if (channel) {
            const ctx = channel.decalContext;
            ctx.clearRect(0, 0, channel.decalCanvas.width, channel.decalCanvas.height);
            this.compositeTexture(index);
        }
    });
    this.updateStatus('Decal layers cleared');
    this.refreshCharacterMaterials();
}

export function updateDecalOpacity(opacity) {
    if (this.currentDecal?.userData) {
        const clampedOpacity = Math.max(0, Math.min(1, opacity));
        this.currentDecal.userData.opacity = clampedOpacity;

        this.updateStatus(`Decal opacity: ${Math.round(clampedOpacity * 100)}%`);
        this.updateProjectiveDecalPreview();
        this.render();
    }
}

export function updateDecalScale(scale) {
    if (this.currentDecal?.userData) {
        this.currentDecal.userData.scale = scale;
        this.currentDecal.userData.decalSize = 0.2 * scale;
        this.updateProjectiveDecalPreview();
    }
}

export function centerModel() {
    if (!this.mesh) return;
    const box = new THREE.Box3().setFromObject(this.mesh);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    this.mesh.position.sub(center);
    const maxDim = Math.max(size.x, size.y, size.z);
    this.mesh.scale.setScalar(2 / maxDim);

    if (this.projective3D) {
        this.projective3D.setMesh(this.mesh);
    }
}
